(() => {
  const registry = window.NipponVinoWineAtlasRenderers = window.NipponVinoWineAtlasRenderers || {};
  const SVG_NS = 'http://www.w3.org/2000/svg';

  const PREFECTURE_NAMES = [
    'Hokkaidō', 'Aomori', 'Iwate', 'Miyagi', 'Akita', 'Yamagata', 'Fukushima',
    'Ibaraki', 'Tochigi', 'Gunma', 'Saitama', 'Chiba', 'Tokio', 'Kanagawa',
    'Niigata', 'Toyama', 'Ishikawa', 'Fukui', 'Yamanashi', 'Nagano', 'Gifu',
    'Shizuoka', 'Aichi', 'Mie', 'Shiga', 'Kyoto', 'Osaka', 'Hyōgo', 'Nara',
    'Wakayama', 'Tottori', 'Shimane', 'Okayama', 'Hiroshima', 'Yamaguchi',
    'Tokushima', 'Kagawa', 'Ehime', 'Kōchi', 'Fukuoka', 'Saga', 'Nagasaki',
    'Kumamoto', 'Ōita', 'Miyazaki', 'Kagoshima', 'Okinawa',
  ];

  class WineAtlasRendererBase {
    constructor() {
      this.geometryById = new Map();
      this.listeners = [];
      this.animationFrame = null;
      this.layoutName = '';
      this.level = 'japan';
      this.selectedId = '';
      this.matchingIds = new Set();
    }

    mount(container, data, state = {}) {
      this.container = container;
      this.data = data;
      this.controller = data.controller;
      this.features = data.features || [];
      this.regions = data.regions || [];
      this.regionByCode = data.regionByCode || new Map();
      this.mapCanvas = data.mapCanvas;
      this.mapStage = data.mapStage;
      this.selectedId = state.selectedId || '';
      this.level = state.level || 'japan';
      this.matchingIds = state.matchingIds || new Set();
    }

    createSvg(width, height, title, description) {
      const svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
      svg.setAttribute('role', 'group');
      svg.setAttribute('aria-label', title);
      svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      const titleNode = document.createElementNS(SVG_NS, 'title');
      titleNode.textContent = title;
      const descNode = document.createElementNS(SVG_NS, 'desc');
      descNode.textContent = description;
      svg.append(titleNode, descNode);
      return svg;
    }

    createElement(name, attributes = {}) {
      const element = document.createElementNS(SVG_NS, name);
      Object.entries(attributes).forEach(([key, value]) => {
        if (value !== null && value !== undefined) element.setAttribute(key, String(value));
      });
      return element;
    }

    prefectureName(code) {
      return PREFECTURE_NAMES[Number(code) - 1] || `Präfektur ${Number(code)}`;
    }

    projectedCoordinates(features, affine = ([x, y]) => [x, y]) {
      const mercator = ([longitude, latitude]) => {
        const clipped = Math.max(-85, Math.min(85, Number(latitude)));
        return [
          Number(longitude) * Math.PI / 180,
          -Math.log(Math.tan((Math.PI / 4) + ((clipped * Math.PI / 180) / 2))),
        ];
      };
      const raw = [];
      features.forEach((feature) => this.eachCoordinate(feature, (coordinate) => raw.push(affine(mercator(coordinate)))));
      return { mercator, raw };
    }

    createProjector(features, frame, options = {}) {
      const affine = options.affine || ((point) => point);
      const { mercator, raw } = this.projectedCoordinates(features, affine);
      const xs = raw.map((point) => point[0]);
      const ys = raw.map((point) => point[1]);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      const padding = Number(options.padding) || 0;
      const content = {
        x: frame.x + padding,
        y: frame.y + padding,
        width: Math.max(1, frame.width - (padding * 2)),
        height: Math.max(1, frame.height - (padding * 2)),
      };
      const scale = Math.min(
        content.width / Math.max(maxX - minX, 0.0001),
        content.height / Math.max(maxY - minY, 0.0001)
      );
      const offsetX = content.x + ((content.width - ((maxX - minX) * scale)) / 2);
      const offsetY = content.y + ((content.height - ((maxY - minY) * scale)) / 2);
      return (coordinate) => {
        const [x, y] = affine(mercator(coordinate));
        return [offsetX + ((x - minX) * scale), offsetY + ((y - minY) * scale)];
      };
    }

    eachCoordinate(feature, callback) {
      const visit = (value) => {
        if (!Array.isArray(value)) return;
        if (value.length >= 2 && Number.isFinite(Number(value[0])) && Number.isFinite(Number(value[1]))) {
          callback(value);
          return;
        }
        value.forEach(visit);
      };
      visit(feature?.geometry?.coordinates);
    }

    featurePath(feature, projector) {
      const polygonPath = (polygon) => polygon.map((ring) => ring.map((coordinate, index) => {
        const [x, y] = projector(coordinate);
        return `${index ? 'L' : 'M'}${x.toFixed(2)},${y.toFixed(2)}`;
      }).join('') + 'Z').join('');
      const coordinates = feature?.geometry?.coordinates || [];
      if (feature?.geometry?.type === 'Polygon') return polygonPath(coordinates);
      return coordinates.map(polygonPath).join('');
    }

    featureBounds(feature, projector) {
      const points = [];
      this.eachCoordinate(feature, (coordinate) => points.push(projector(coordinate)));
      if (!points.length) return null;
      const xs = points.map((point) => point[0]);
      const ys = points.map((point) => point[1]);
      const x = Math.min(...xs);
      const y = Math.min(...ys);
      return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
    }

    unionBounds(items) {
      const bounds = items.filter(Boolean);
      if (!bounds.length) return null;
      const x = Math.min(...bounds.map((item) => item.x));
      const y = Math.min(...bounds.map((item) => item.y));
      const right = Math.max(...bounds.map((item) => item.x + item.width));
      const bottom = Math.max(...bounds.map((item) => item.y + item.height));
      return { x, y, width: right - x, height: bottom - y };
    }

    fitBounds(bounds, paddingFactor = 0.08) {
      if (!bounds) return this.originalViewBox;
      const padding = Math.max(10, Math.max(bounds.width, bounds.height) * paddingFactor);
      let x = bounds.x - padding;
      let y = bounds.y - padding;
      let width = bounds.width + (padding * 2);
      let height = bounds.height + (padding * 2);
      const canvasWidth = this.mapCanvas?.clientWidth || width;
      const canvasHeight = this.mapCanvas?.clientHeight || height;
      const canvasRatio = canvasWidth / Math.max(canvasHeight, 1);
      const boundsRatio = width / Math.max(height, 1);
      if (boundsRatio > canvasRatio) {
        const nextHeight = width / canvasRatio;
        y -= (nextHeight - height) / 2;
        height = nextHeight;
      } else {
        const nextWidth = height * canvasRatio;
        x -= (nextWidth - width) / 2;
        width = nextWidth;
      }
      return { x, y, width, height };
    }

    animateViewBox(target, animate = true) {
      if (!this.svg || !target) return;
      if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
      const current = this.svg.viewBox.baseVal;
      const from = { x: current.x, y: current.y, width: current.width, height: current.height };
      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      const duration = animate && !reduced ? 280 : 0;
      const set = (box) => this.svg?.setAttribute('viewBox', `${box.x} ${box.y} ${box.width} ${box.height}`);
      if (!duration) {
        set(target);
        return;
      }
      const started = performance.now();
      const tick = (now) => {
        const progress = Math.min(1, (now - started) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        set({
          x: from.x + ((target.x - from.x) * eased),
          y: from.y + ((target.y - from.y) * eased),
          width: from.width + ((target.width - from.width) * eased),
          height: from.height + ((target.height - from.height) * eased),
        });
        if (progress < 1) this.animationFrame = requestAnimationFrame(tick);
      };
      this.animationFrame = requestAnimationFrame(tick);
    }

    regionForFeature(feature) {
      const code = String(feature?.properties?.code || '').padStart(2, '0');
      const region = this.regionByCode.get(code);
      return {
        code,
        region,
        id: region?.id || `prefecture-${code}`,
        name: region?.name || this.prefectureName(code),
        nameJa: feature?.properties?.nameJa || '',
      };
    }

    selectFeature(feature, options = {}) {
      const item = this.regionForFeature(feature);
      if (item.region) this.controller.selectRegion(item.id, options);
      else this.controller.selectPrefecture(item.code, item.name, item.nameJa, options);
    }

    bindInteractive(element, feature) {
      const item = this.regionForFeature(feature);
      element.dataset.atlasRegion = item.id;
      element.dataset.prefectureCode = item.code;
      element.setAttribute('role', 'button');
      element.setAttribute('tabindex', '0');
      element.setAttribute('aria-label', `${item.name} auswählen und Regionsprofil öffnen`);
      element.setAttribute('aria-pressed', String(item.id === this.selectedId));
      const onClick = (event) => {
        event.stopPropagation();
        this.selectFeature(feature, { focusDetail: false });
      };
      const onKeydown = (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.selectFeature(feature, { focusDetail: true });
      };
      const onEnter = () => this.controller.showTooltip(item.region || {
        name: item.name,
        summary: 'Kurzprofil dieser Präfektur öffnen.',
        availableWines: 0,
        wineryCount: null,
        isFallback: true,
      });
      const onLeave = () => this.controller.hideTooltip();
      element.addEventListener('click', onClick);
      element.addEventListener('keydown', onKeydown);
      element.addEventListener('pointerenter', onEnter);
      element.addEventListener('pointerleave', onLeave);
      this.listeners.push([element, 'click', onClick], [element, 'keydown', onKeydown], [element, 'pointerenter', onEnter], [element, 'pointerleave', onLeave]);
    }

    update(matchingIds, state = {}) {
      this.matchingIds = matchingIds || new Set();
      this.selectedId = state.selectedId || '';
      this.level = state.level || this.level;
      this.svg?.querySelectorAll('[data-atlas-region]').forEach((element) => {
        const id = element.dataset.atlasRegion;
        const region = this.controller.regionById.get(id);
        const selected = id === this.selectedId;
        element.classList.toggle('atlas-match', this.matchingIds.has(id));
        element.classList.toggle('atlas-available', (region?.availableWines || 0) > 0);
        element.classList.toggle('atlas-selected', selected);
        element.classList.toggle('atlas-filtered-out', !this.matchingIds.has(id));
        element.setAttribute('aria-pressed', String(selected));
      });
    }

    selectRegion(regionId) {
      this.selectedId = regionId || '';
      this.update(this.matchingIds, { selectedId: this.selectedId, level: this.level });
    }

    setLevel(level, options = {}) {
      this.level = ['japan', 'region', 'detail'].includes(level) ? level : 'japan';
      const selected = this.geometryById.get(this.selectedId);
      const target = this.level === 'japan' || !selected
        ? this.originalViewBox
        : this.fitBounds(selected, this.level === 'detail' ? 0.55 : 1.25);
      this.animateViewBox(target, options.animate !== false);
    }

    resize() {}

    destroy() {
      this.listeners.forEach(([element, type, listener]) => element.removeEventListener(type, listener));
      this.listeners = [];
      if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
      this.svg = null;
      this.container = null;
    }
  }

  registry.Base = WineAtlasRendererBase;
  registry.prefectureNames = PREFECTURE_NAMES;
})();
